import React, { useState, useEffect, useRef } from 'react';
import { useClient } from '../contexts/ClientContext';
import Logo from './Logo';

export interface LeadModalProfile {
    nom: string;
    prenom: string;
    phone: string;
    address?: string;
    email?: string;
    password?: string;
    isLogin?: boolean;
}

interface MandatoryLeadModalProps {
    isOpen: boolean;
    onSuccess: (profile: LeadModalProfile) => void;
}

export const MandatoryLeadModal: React.FC<MandatoryLeadModalProps> = ({ isOpen, onSuccess }) => {
    const { loginClient } = useClient();

    // Mode : false = Inscription avec Mot de Passe / true = Connexion Client Existant
    const [isLoginMode, setIsLoginMode] = useState(false);

    // Champs Inscription
    const [nom, setNom] = useState('');
    const [prenom, setPrenom] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);

    // Champs Connexion
    const [loginIdentifier, setLoginIdentifier] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const prenomInputRef = useRef<HTMLInputElement>(null);
    const loginInputRef = useRef<HTMLInputElement>(null);

    // Verrouillage total et inconditionnel du scroll de la page
    useEffect(() => {
        if (!isOpen) return;

        const prevOverflow = document.body.style.overflow;
        const prevPosition = document.body.style.position;
        document.body.style.overflow = 'hidden';

        const timer = setTimeout(() => {
            if (isLoginMode) {
                loginInputRef.current?.focus();
            } else {
                prenomInputRef.current?.focus();
            }
        }, 150);

        // Bloquer la touche Echap
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.keyCode === 27) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.position = prevPosition;
            window.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(timer);
        };
    }, [isOpen, isLoginMode]);

    if (!isOpen) return null;

    // --- SOUMISSION INSCRIPTION OBLIGATOIRE ---
    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg(null);

        const cleanPrenom = prenom.trim();
        const cleanNom = nom.trim();
        const cleanPhone = phone.trim();
        const cleanAddress = address.trim();
        const cleanEmail = email.trim();
        const cleanPassword = password.trim();
        const rawDigits = cleanPhone.replace(/\D/g, '');

        if (!cleanPrenom || cleanPrenom.length < 2) {
            setErrorMsg('Veuillez renseigner un prénom valide (au moins 2 lettres).');
            return;
        }

        if (!cleanNom || cleanNom.length < 2) {
            setErrorMsg('Veuillez renseigner un nom de famille valide (au moins 2 lettres).');
            return;
        }

        if (!rawDigits || rawDigits.length < 8) {
            setErrorMsg('Veuillez renseigner un numéro de téléphone valide (au moins 8 chiffres).');
            return;
        }

        if (!cleanAddress || cleanAddress.length < 3) {
            setErrorMsg('Veuillez renseigner votre adresse postale ou votre ville.');
            return;
        }

        if (!cleanPassword || cleanPassword.length < 4) {
            setErrorMsg("Veuillez choisir un mot de passe d'au moins 4 caractères.");
            return;
        }

        if (cleanEmail && (!cleanEmail.includes('@') || !cleanEmail.includes('.'))) {
            setErrorMsg('Veuillez renseigner une adresse email valide (ou laisser le champ vide).');
            return;
        }

        setIsSubmitting(true);
        const fullName = `${cleanPrenom} ${cleanNom}`;

        try {
            fetch('/api/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nom: cleanNom,
                    prenom: cleanPrenom,
                    name: fullName,
                    phone: cleanPhone,
                    address: cleanAddress,
                    email: cleanEmail || undefined,
                    password: cleanPassword
                })
            }).catch(() => {});
        } catch (err) {}

        setIsSubmitting(false);

        try {
            localStorage.setItem('astro_user_registered_v2', 'true');
        } catch (e) {}

        onSuccess({
            nom: cleanNom,
            prenom: cleanPrenom,
            phone: cleanPhone,
            address: cleanAddress,
            email: cleanEmail || undefined,
            password: cleanPassword,
            isLogin: false
        });
    };

    // --- SOUMISSION CONNEXION CLIENT EXISTANT ---
    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg(null);

        const cleanIdentifier = loginIdentifier.trim();
        if (!cleanIdentifier) {
            setErrorMsg('Veuillez renseigner votre identifiant (email ou téléphone).');
            return;
        }

        if (!loginPassword) {
            setErrorMsg('Veuillez renseigner votre mot de passe.');
            return;
        }

        setIsSubmitting(true);
        const res = loginClient(cleanIdentifier, loginPassword);
        setIsSubmitting(false);

        if (res && res.success) {
            try {
                localStorage.setItem('astro_user_registered_v2', 'true');
            } catch (e) {}
            onSuccess({
                nom: '',
                prenom: cleanIdentifier,
                phone: '',
                isLogin: true
            });
        } else {
            setErrorMsg(res?.message || 'Identifiant ou mot de passe incorrect. Vérifiez vos informations.');
        }
    };

    return (
        <div 
            className="fixed inset-0 z-[9999999] flex items-center justify-center p-3 sm:p-5 bg-black/90 backdrop-blur-2xl select-none overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-modal-title"
        >
            {/* STYLES D'ANIMATION PERSONNALISÉS */}
            <style>{`
                @keyframes cosmicPulse {
                    0%, 100% { transform: scale(1); opacity: 0.7; }
                    50% { transform: scale(1.08); opacity: 1; }
                }
                @keyframes starTwinkle {
                    0%, 100% { opacity: 0.2; transform: scale(0.8); }
                    50% { opacity: 1; transform: scale(1.2); }
                }
                @keyframes shimmerSweep {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(200%); }
                }
                @keyframes borderGlow {
                    0%, 100% { border-color: rgba(245, 158, 11, 0.4); box-shadow: 0 0 35px rgba(245, 158, 11, 0.2), inset 0 0 20px rgba(108, 43, 217, 0.25); }
                    50% { border-color: rgba(251, 191, 36, 0.8); box-shadow: 0 0 55px rgba(251, 191, 36, 0.35), inset 0 0 30px rgba(245, 158, 11, 0.2); }
                }
                .cosmic-glow-border {
                    animation: borderGlow 4s ease-in-out infinite;
                }
                .btn-shimmer-effect {
                    position: relative;
                    overflow: hidden;
                }
                .btn-shimmer-effect::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 60%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
                    transform: skewX(-20deg);
                    animation: shimmerSweep 3s infinite;
                }
            `}</style>

            {/* AURA LUMINEUSE D'ARRIÈRE-PLAN */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
                <div className="w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-amber-500/20 via-purple-600/20 to-indigo-500/15 blur-[140px] animate-pulse"></div>
                <div className="absolute w-[350px] h-[350px] rounded-full bg-amber-400/15 blur-[100px]" style={{ animation: 'cosmicPulse 6s ease-in-out infinite' }}></div>
                
                {/* Étoiles décoratives flottantes */}
                <div className="absolute top-1/4 left-1/4 text-amber-300 text-sm" style={{ animation: 'starTwinkle 3s infinite' }}>✦</div>
                <div className="absolute top-1/3 right-1/4 text-purple-300 text-xs" style={{ animation: 'starTwinkle 4s 1s infinite' }}>✧</div>
                <div className="absolute bottom-1/4 left-1/3 text-amber-200 text-xs" style={{ animation: 'starTwinkle 3.5s 0.5s infinite' }}>✦</div>
                <div className="absolute bottom-1/3 right-1/3 text-yellow-300 text-sm" style={{ animation: 'starTwinkle 4.5s 1.5s infinite' }}>✧</div>
            </div>

            {/* CARTE PRINCIPALE ULTRA-ATTRAYANTE */}
            <div className="relative w-full max-w-[460px] my-auto bg-gradient-to-b from-gray-900/95 via-[#0c091f]/95 to-black/98 border-2 border-amber-500/50 rounded-[28px] p-5 sm:p-7 shadow-2xl cosmic-glow-border text-center">
                
                {/* RAYON SUPÉRIEUR DORÉ */}
                <div className="absolute -top-[2px] left-1/2 -translate-x-1/2 w-44 h-[3px] bg-gradient-to-r from-transparent via-amber-300 to-transparent shadow-[0_0_15px_#f59e0b]"></div>

                {/* 1. EN-TÊTE : LOGO OFFICIEL DU SITE AVEC LUEUR ASTRALE */}
                <div className="flex flex-col items-center justify-center mb-3">
                    <div className="relative p-2 rounded-2xl bg-gradient-to-b from-white/[0.06] to-transparent border border-amber-400/20 shadow-[0_0_30px_rgba(245,158,11,0.15)] mb-2">
                        <Logo size="md" showTagline={true} />
                    </div>

                    {/* BADGE ACCROCHEUR CADEAU / BIENVENUE */}
                    {!isLoginMode ? (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 via-yellow-400/25 to-amber-500/20 border border-amber-400/50 text-amber-300 text-[11px] sm:text-xs font-black tracking-wide shadow-[0_0_15px_rgba(251,191,36,0.3)] animate-pulse">
                            <span>🎁</span>
                            <span>OFFRE DE BIENVENUE : TIRAGE & PRÉDICTIONS OFFERTS</span>
                            <span>✨</span>
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-200 text-[11px] sm:text-xs font-bold tracking-wide">
                            <span>🔐</span>
                            <span>ESPACE CLIENT SÉCURISÉ</span>
                        </div>
                    )}
                </div>

                {/* TITRE & SOUS-TITRE INCITATIFS */}
                <div className="space-y-1 mb-4">
                    <h2 id="lead-modal-title" className="text-xl sm:text-2xl font-black text-white tracking-tight">
                        {isLoginMode ? 'Bon retour parmi nous !' : 'Débloquez Votre Avenir en Direct'}
                    </h2>

                    <p className="text-xs sm:text-[13px] text-gray-300 leading-relaxed max-w-sm mx-auto">
                        {isLoginMode 
                            ? 'Connectez-vous pour retrouver vos voyants favoris, vos messages et vos consultations.'
                            : 'Inscrivez-vous en 30 secondes pour accéder à nos voyants et recevoir vos premières réponses en direct.'}
                    </p>

                    {/* MICRO-TICKER DE RÉASSURANCE */}
                    {!isLoginMode && (
                        <div className="pt-1 flex items-center justify-center gap-2 text-[10px] text-amber-200/90 font-medium">
                            <span className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                                <span className="text-emerald-300 font-bold">12 Voyants en ligne</span>
                            </span>
                            <span>•</span>
                            <span>⚡ Réponse immédiate</span>
                            <span>•</span>
                            <span>🔒 100% Confidentiel</span>
                        </div>
                    )}
                </div>

                {/* MESSAGE D'ERREUR ÉVENTUEL */}
                {errorMsg && (
                    <div className="mb-3.5 p-3 rounded-xl bg-red-950/80 border border-red-500/60 text-red-200 text-xs font-bold flex items-center gap-2 text-left animate-shake shadow-lg">
                        <span className="text-base shrink-0">⚠️</span>
                        <span>{errorMsg}</span>
                    </div>
                )}

                {/* ========================================================================= */}
                {/* MODE 1 : FORMULAIRE D'INSCRIPTION ULTRA-ATTRAYANT                         */}
                {/* ========================================================================= */}
                {!isLoginMode ? (
                    <form onSubmit={handleRegisterSubmit} className="space-y-2.5 sm:space-y-3 text-left">
                        {/* PRÉNOM & NOM */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1">
                                    <span>Prénom</span>
                                    <span className="text-amber-400">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        ref={prenomInputRef}
                                        type="text"
                                        required
                                        autoComplete="given-name"
                                        placeholder="Sophie"
                                        value={prenom}
                                        onChange={(e) => setPrenom(e.target.value)}
                                        className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-8 transition-all outline-none font-medium"
                                    />
                                    <span className="absolute left-2.5 top-2 sm:top-2.5 text-xs text-amber-300/80 pointer-events-none">
                                        👤
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1">
                                    <span>Nom</span>
                                    <span className="text-amber-400">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        required
                                        autoComplete="family-name"
                                        placeholder="Martin"
                                        value={nom}
                                        onChange={(e) => setNom(e.target.value)}
                                        className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-8 transition-all outline-none font-medium"
                                    />
                                    <span className="absolute left-2.5 top-2 sm:top-2.5 text-xs text-amber-300/80 pointer-events-none">
                                        👥
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* TÉLÉPHONE */}
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Numéro de Téléphone</span>
                                    <span className="text-amber-400">*</span>
                                </span>
                                <span className="text-[10px] text-amber-300/90 font-mono">Mobile ou Fixe</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="tel"
                                    required
                                    autoComplete="tel"
                                    placeholder="06 12 34 56 78"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2 sm:top-2.5 text-sm pointer-events-none">
                                    📱
                                </span>
                            </div>
                        </div>

                        {/* ADRESSE POSTALE / VILLE */}
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Adresse postale ou Ville</span>
                                    <span className="text-amber-400">*</span>
                                </span>
                                <span className="text-[10px] text-amber-300/90 font-mono">France & Dom</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    required
                                    autoComplete="street-address"
                                    placeholder="Ex: 12 Rue de la Paix, 75002 Paris"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2 sm:top-2.5 text-sm pointer-events-none">
                                    📍
                                </span>
                            </div>
                        </div>

                        {/* EMAIL (OPTIONNEL) */}
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Adresse Email</span>
                                    <span className="text-gray-400 text-[10px] font-normal">(Optionnel)</span>
                                </span>
                                <span className="text-[10px] text-gray-400 font-mono">Facultatif</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    autoComplete="email"
                                    placeholder="contact@exemple.fr (facultatif)"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/20 hover:border-amber-400/40 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2 sm:top-2.5 text-sm pointer-events-none">
                                    ✉️
                                </span>
                            </div>
                        </div>

                        {/* MOT DE PASSE (CRÉATION OBLIGATOIRE) */}
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Créer votre mot de passe</span>
                                    <span className="text-amber-400">*</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                    className="text-[10px] text-amber-300 hover:text-white cursor-pointer font-bold"
                                >
                                    {showRegisterPassword ? 'Masquer 👁️' : 'Afficher 👁️‍🗨️'}
                                </button>
                            </label>
                            <div className="relative">
                                <input
                                    type={showRegisterPassword ? 'text' : 'password'}
                                    required
                                    autoComplete="new-password"
                                    placeholder="Choisissez un mot de passe (min. 4 car.)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2 sm:py-2.5 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2 sm:top-2.5 text-sm pointer-events-none">
                                    🔒
                                </span>
                            </div>
                        </div>

                        {/* BOUTON MAGISTRAL S'INSCRIRE AVEC EFFET SHIMMER */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-shimmer-effect w-full mt-2 py-3.5 sm:py-4 px-4 rounded-2xl bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-300 hover:via-yellow-200 hover:to-amber-400 text-black font-black text-sm sm:text-base shadow-[0_4px_25px_rgba(245,158,11,0.5)] hover:shadow-[0_6px_35px_rgba(245,158,11,0.7)] flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-[0.98] hover:scale-[1.01] disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
                                    <span>Création de votre accès...</span>
                                </>
                            ) : (
                                <>
                                    <span>ACCÉDER AU SITE & RECEVOIR MES PRÉDICTIONS</span>
                                    <span className="text-lg">✨</span>
                                </>
                            )}
                        </button>

                        {/* BOUTON CLAIR DÉJÀ CLIENT */}
                        <div className="pt-1.5 text-center">
                            <button
                                type="button"
                                onClick={() => { setIsLoginMode(true); setErrorMsg(null); }}
                                className="w-full py-2.5 px-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-amber-400/30 hover:border-amber-400 text-amber-200 hover:text-white text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                            >
                                <span>🔑 Déjà client ?</span>
                                <span className="underline decoration-amber-400 font-black">Connectez-vous en 1 clic ➔</span>
                            </button>
                        </div>
                    </form>
                ) : (
                    /* ========================================================================= */
                    /* MODE 2 : FORMULAIRE DE CONNEXION POUR CLIENT EXISTANT                     */
                    /* ========================================================================= */
                    <form onSubmit={handleLoginSubmit} className="space-y-3 sm:space-y-3.5 text-left">
                        {/* IDENTIFIANT : EMAIL OU TÉLÉPHONE */}
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Identifiant</span>
                                    <span className="text-amber-400">*</span>
                                </span>
                                <span className="text-[10px] text-amber-300/90 font-mono">Email ou Téléphone</span>
                            </label>
                            <div className="relative">
                                <input
                                    ref={loginInputRef}
                                    type="text"
                                    required
                                    autoComplete="username"
                                    placeholder="Votre numéro ou adresse email"
                                    value={loginIdentifier}
                                    onChange={(e) => setLoginIdentifier(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2.5 sm:py-3 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2.5 sm:top-3 text-sm pointer-events-none">
                                    👤
                                </span>
                            </div>
                        </div>

                        {/* MOT DE PASSE */}
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-300 flex items-center justify-between">
                                <span className="flex items-center gap-1">
                                    <span>Mot de passe</span>
                                    <span className="text-amber-400">*</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                                    className="text-[10px] text-amber-300 hover:text-white cursor-pointer font-bold"
                                >
                                    {showLoginPassword ? 'Masquer 👁️' : 'Afficher 👁️‍🗨️'}
                                </button>
                            </label>
                            <div className="relative">
                                <input
                                    type={showLoginPassword ? 'text' : 'password'}
                                    required
                                    autoComplete="current-password"
                                    placeholder="Votre mot de passe"
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    className="w-full bg-black/60 border border-amber-500/30 hover:border-amber-400/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 text-white placeholder-gray-500 text-xs sm:text-sm rounded-xl px-3 py-2.5 sm:py-3 pl-9 transition-all outline-none font-medium"
                                />
                                <span className="absolute left-3 top-2.5 sm:top-3 text-sm pointer-events-none">
                                    🔒
                                </span>
                            </div>
                        </div>

                        {/* BOUTON SE CONNECTER */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-shimmer-effect w-full mt-2 py-3.5 sm:py-4 px-4 rounded-2xl bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-300 hover:via-yellow-200 hover:to-amber-400 text-black font-black text-sm sm:text-base shadow-[0_4px_25px_rgba(245,158,11,0.5)] hover:shadow-[0_6px_35px_rgba(245,158,11,0.7)] flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-[0.98] hover:scale-[1.01] disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
                                    <span>Connexion à votre espace...</span>
                                </>
                            ) : (
                                <>
                                    <span>SE CONNECTER À MON ESPACE</span>
                                    <span className="text-lg">🔑</span>
                                </>
                            )}
                        </button>

                        {/* BOUTON RETOUR INSCRIPTION */}
                        <div className="pt-1.5 text-center">
                            <button
                                type="button"
                                onClick={() => { setIsLoginMode(false); setErrorMsg(null); }}
                                className="w-full py-2.5 px-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-amber-400/30 hover:border-amber-400 text-amber-200 hover:text-white text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                            >
                                <span>✨ Nouveau visiteur ?</span>
                                <span className="underline decoration-amber-400 font-black">Créer un compte & débloquer l'accès ➔</span>
                            </button>
                        </div>
                    </form>
                )}

                {/* PIED DE CARTE : SÉCURITÉ & RÉASSURANCE */}
                <div className="mt-3.5 pt-3 border-t border-amber-500/20 flex items-center justify-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-gray-300 font-medium">
                    <div className="flex items-center gap-1 text-emerald-300">
                        <span>🔒</span>
                        <span>100% Anonyme</span>
                    </div>
                    <span className="text-gray-600">•</span>
                    <div className="flex items-center gap-1 text-amber-300">
                        <span>⚡</span>
                        <span>Accès Immédiat</span>
                    </div>
                    <span className="text-gray-600">•</span>
                    <div className="flex items-center gap-1 text-purple-300">
                        <span>📜</span>
                        <span>Conforme RGPD</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MandatoryLeadModal;
